import React from 'react';

import { useRouter } from 'expo-router';
import { StyleSheet } from 'react-native';

import ActionButton, { IActionButtonProps } from 'components/action-button';
import { StyledLoyaltyPointsIcon } from 'components/loyalty-points-button/styled';
import { Box, Typography } from 'components/ui';
import { useLoyaltyPoints } from 'hooks/loyalty/use-loyalty-points';
import { StyledRewardsLogo } from 'pages/account/account-loyalty-header.styled';
import { CustomEventNames } from 'state/amplitude';
import { useAuthContext } from 'state/auth';
import { t } from 'state/intl/translations';
import { useIsLoyaltyEnabled } from 'state/loyalty/hooks/use-is-loyalty-enabled';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { primitive, spacing } from 'styles/constants';
import { routes } from 'utils/routing';

const SignUpActionButton = ({ onPress }: Pick<IActionButtonProps, 'onPress'>) => {
  return (
    <ActionButton
      width={'$56'}
      height={'$12'}
      alignSelf={'center'}
      testID="account-sign-up-link"
      eventName={CustomEventNames.BUTTON_CLICK_SIGN_UP}
      onPress={onPress}
    >
      {t('signUpNow')}
    </ActionButton>
  );
};

const RedeemPointsActionButton = ({ onPress }: Pick<IActionButtonProps, 'onPress'>) => {
  const router = useRouter();
  return (
    <ActionButton
      width={'$56'}
      minHeight={'$12'}
      alignSelf={'center'}
      testID="account-sign-up-link"
      backgroundColor={primitive.$meltyYellow}
      color={primitive.$bbqBrown}
      eventName={CustomEventNames.BUTTON_CLICK_REDEEM_NOW}
      onPress={() => router.replace(routes.rewards)}
    >
      {t('redeemNow')}
    </ActionButton>
  );
};

const normalizeAccessibilityLabel = (label: string) => label.replace(/\s+/g, ' ').trim();

export const AccountLoyaltyHeader = () => {
  const loyaltyEnabled = useIsLoyaltyEnabled();
  const { isAuthenticated, openSignInModal } = useAuthContext();
  const { loyaltyUser } = useLoyaltyUser();
  const {
    userHasExpiringPoints,
    pointsExpirationDate,
    pointsToExpire,
    isNewPointsModel,
    currentPoints,
  } = useLoyaltyPoints(loyaltyUser);

  const loyaltyCreateAccountHeadline = loyaltyEnabled
    ? t('loyaltyCreateAccountHeadline')
    : t('createAccountHeadline');
  const availablePointsAccessibilityLabel = t('accountRewardsPoints', {
    points: currentPoints,
  });
  const expiringPointsAccessibilityLabel = userHasExpiringPoints
    ? isNewPointsModel
      ? t('accountRewardsPointsExpiration', {
          pointsToExpire,
          expirationDateTime: pointsExpirationDate.formatted,
        })
      : `${t('yourPointsWillExpire')} ${pointsExpirationDate.formatted}`
    : '';
  const pointsSummaryAccessibilityLabel = [
    availablePointsAccessibilityLabel,
    expiringPointsAccessibilityLabel,
  ]
    .map(normalizeAccessibilityLabel)
    .filter(Boolean)
    .join(' ');

  return (
    <Box style={styles.container}>
      {!isAuthenticated && (
        <Typography variant="headerOne" style={styles.headline} testID="account-sign-up-label">
          {loyaltyCreateAccountHeadline}
        </Typography>
      )}

      {loyaltyEnabled && (
        <StyledRewardsLogo
          testID="account-sign-up-loyalty-logo"
          maxWidth={'$80'}
          alignSelf={'center'}
        />
      )}

      {isAuthenticated && loyaltyUser && (
        <Box
          accessible
          accessibilityLabel={pointsSummaryAccessibilityLabel}
          accessibilityRole="text"
          focusable
          style={styles.pointsSummaryWrapper}
          testID="account-loyalty-points-summary"
        >
          <Box style={styles.pointsRow}>
            <Typography fontSize="xl" bold lineHeight="2xl" style={styles.pointsIntro}>
              {t('youHave')}
            </Typography>
            <Box style={styles.pointsPill}>
              <StyledLoyaltyPointsIcon marginRight={'$1'} alignSelf={'center'} reversed={false} />
              <Typography
                color={primitive.$white}
                fontSize="xl"
                bold
                style={{ alignContent: 'center' }}
              >
                {String(currentPoints)}
              </Typography>
            </Box>
            <Typography fontSize="xl" bold lineHeight="2xl" style={{ alignSelf: 'center' }}>
              {t('available').toLowerCase()}
            </Typography>
          </Box>

          {userHasExpiringPoints && (
            <>
              {isNewPointsModel ? (
                <Typography numberOfLines={2} style={{ alignSelf: 'center' }}>
                  {t('accountRewardsPointsExpiration', {
                    pointsToExpire,
                    expirationDateTime: pointsExpirationDate.formatted,
                  })}
                </Typography>
              ) : (
                // This branch is required here because CA users are still using the old expiration model
                // once CA users are migrated to the new model, this branch can be removed
                <Typography textAlign="center" numberOfLines={2} style={{ alignSelf: 'center' }}>
                  {t('yourPointsWillExpire')}
                  {'\n'}
                  {pointsExpirationDate.formatted}
                </Typography>
              )}
            </>
          )}
        </Box>
      )}

      {!isAuthenticated && <SignUpActionButton onPress={() => openSignInModal()} />}
      {isAuthenticated && userHasExpiringPoints && <RedeemPointsActionButton />}
    </Box>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: spacing.$8,
    marginHorizontal: spacing.$3,
    marginTop: 0,
  },
  headline: {
    marginBottom: 0,
    textAlign: 'center',
  },
  pointsPill: {
    alignItems: 'center',
    backgroundColor: primitive.$bbqBrown,
    borderRadius: 4,
    flexDirection: 'row',
    marginRight: spacing.$1,
    padding: spacing.$0_5,
  },
  pointsIntro: {
    alignSelf: 'center',
    marginRight: spacing.$1,
  },
  pointsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  pointsSummaryWrapper: {
    marginBottom: spacing.$4,
  },
});
